import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  FiUsers, FiCalendar, FiBriefcase, FiAward,
  FiAlertCircle, FiExternalLink, FiCheckCircle, FiClock,
  FiFileText, FiList, FiDollarSign,
} from 'react-icons/fi';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { PainelResultado } from '../../components/layout/PainelResultado';
import { useAuth } from '../../contexts/AuthContext';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  pssPortalService, STATUS_PSS_LABEL, ESCOLARIDADE_LABEL,
  type EditalPublico,
} from '../../services/pss.service';

export function PssEditalDetalhePage() {
  const { numero } = useParams<{ numero: string }>();
  const navigate = useNavigate();
  const { cidadao, cadastroCompleto } = useAuth();

  const [edital, setEdital] = useState<EditalPublico | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [jaInscrito, setJaInscrito] = useState(false);

  useEffect(() => {
    if (!numero) return;
    pssPortalService.buscarEdital(getEstabelecimentoAtualId(), numero)
      .then(setEdital)
      .finally(() => setCarregando(false));
  }, [numero]);

  useEffect(() => {
    if (!cidadao || !edital) return;
    const editalId = edital.id;
    pssPortalService.minhasInscricoes()
      .then((lista) => {
        if (lista.some((i) => i.edital.id === editalId)) {
          setJaInscrito(true);
        }
      })
      .catch(() => undefined);
  }, [cidadao, edital]);

  if (carregando) {
    return (
      <PortalPage
        titulo="Processo seletivo"
        icone={<FiFileText size={26} />}
        largura="media"
        voltarHref="/pss"
        voltarLabel="Voltar para a lista"
      >
        <div className="flex justify-center py-20"><Spinner tamanho="lg" /></div>
      </PortalPage>
    );
  }

  if (!edital) {
    return (
      <PainelResultado
        titulo="Edital não encontrado"
        icone={<FiAlertCircle size={32} />}
        tom="aviso"
        acoes={
          <Button variant="outline" onClick={() => navigate('/pss')}>
            <FiBriefcase size={14} /> Voltar para a lista
          </Button>
        }
      />
    );
  }

  const aberto = edital.status_pss === 'aberto';
  const hoje = new Date();
  const dentroPrazo = !edital.inscricoes_fim || parseISO(edital.inscricoes_fim) >= hoje;

  function botaoInscrever() {
    if (jaInscrito) {
      return (
        <Button variant="outline" disabled>
          <FiCheckCircle size={14} /> Você já está inscrito
        </Button>
      );
    }
    if (!aberto || !dentroPrazo) {
      return (
        <Button variant="outline" disabled>
          <FiClock size={14} /> Inscrições fechadas
        </Button>
      );
    }
    if (!cidadao) {
      return (
        <Button onClick={() => navigate('/login')}>
          Fazer login para se inscrever
        </Button>
      );
    }
    if (!cadastroCompleto) {
      return (
        <Button onClick={() => navigate('/completar-cadastro')}>
          Completar cadastro para se inscrever
        </Button>
      );
    }
    return (
      <Button onClick={() => navigate(`/pss/${edital!.numero}/inscrever`)}>
        <FiAward size={14} /> Inscrever-se
      </Button>
    );
  }

  return (
    <PortalPage
      titulo={edital.titulo}
      subtitulo={edital.cargo ?? undefined}
      icone={<FiFileText size={26} />}
      largura="media"
      voltarHref="/pss"
      voltarLabel="Voltar para a lista"
      heroExtra={
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs font-medium text-blue-100/90 tabular-nums">{edital.numero}</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            aberto ? 'bg-emerald-100 text-emerald-700' :
            edital.status_pss === 'cancelado' ? 'bg-red-100 text-red-700' :
            edital.status_pss === 'homologado' ? 'bg-blue-100 text-blue-700' :
            'bg-slate-100 text-slate-700'
          }`}>
            {STATUS_PSS_LABEL[edital.status_pss]}
          </span>
        </div>
      }
    >
      <div className="space-y-5">
        {edital.descricao && (
          <SectionCard titulo="Descrição" icone={<FiFileText size={18} />}>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{edital.descricao}</p>
          </SectionCard>
        )}

        <SectionCard titulo="Vagas" icone={<FiUsers size={18} />}>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-semibold text-slate-900">{edital.vagas ?? '—'}</div>
              <div className="text-xs text-slate-500 mt-1">Ampla concorrência</div>
            </div>
            <div>
              <div className="text-3xl font-semibold text-slate-900">{edital.vagas_pcd ?? '—'}</div>
              <div className="text-xs text-slate-500 mt-1">Vagas PCD</div>
            </div>
            <div>
              <div className="text-3xl font-semibold text-slate-900">{edital.cadastro_reserva ?? '—'}</div>
              <div className="text-xs text-slate-500 mt-1">Cadastro reserva</div>
            </div>
          </div>
        </SectionCard>

        {edital.requisitos && (
          <SectionCard titulo="Requisitos" icone={<FiList size={18} />}>
            {edital.escolaridade_minima && (
              <div className="mb-2">
                <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                  {ESCOLARIDADE_LABEL[edital.escolaridade_minima]}
                </span>
              </div>
            )}
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{edital.requisitos}</p>
          </SectionCard>
        )}

        <SectionCard titulo="Cronograma" icone={<FiCalendar size={18} />}>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {edital.inscricoes_inicio && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Inscrições — início</dt>
                <dd className="text-slate-900 mt-0.5">{format(parseISO(edital.inscricoes_inicio), 'dd/MM/yyyy', { locale: ptBR })}</dd>
              </div>
            )}
            {edital.inscricoes_fim && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Inscrições — fim</dt>
                <dd className="text-slate-900 mt-0.5">{format(parseISO(edital.inscricoes_fim), 'dd/MM/yyyy', { locale: ptBR })}</dd>
              </div>
            )}
            {edital.data_prova && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Data da prova</dt>
                <dd className="text-slate-900 mt-0.5">{format(parseISO(edital.data_prova), 'dd/MM/yyyy', { locale: ptBR })}</dd>
              </div>
            )}
            {edital.local_prova && (
              <div>
                <dt className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Local da prova</dt>
                <dd className="text-slate-900 mt-0.5">{edital.local_prova}</dd>
              </div>
            )}
          </dl>
        </SectionCard>

        {(edital.valor_taxa != null || edital.permite_isencao) && (
          <SectionCard titulo="Taxa de inscrição" icone={<FiDollarSign size={18} />}>
            <p className="text-sm text-slate-900">
              {edital.valor_taxa === 0 || edital.valor_taxa == null
                ? 'Inscrição gratuita'
                : `R$ ${edital.valor_taxa.toFixed(2).replace('.', ',')}`}
            </p>
            {edital.permite_isencao && (
              <p className="text-xs text-slate-600 mt-1">
                Isenção disponível conforme Lei 13.656/2018 para candidatos hipossuficientes.
              </p>
            )}
          </SectionCard>
        )}

        {edital.edital_pdf_url && (
          <SectionCard>
            <a
              href={edital.edital_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-institutional hover:underline font-medium"
            >
              <FiExternalLink size={14} /> Baixar edital completo (PDF)
            </a>
          </SectionCard>
        )}

        <div className="flex items-center justify-between gap-3 mt-6 sticky bottom-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-md">
          <div className="text-sm text-slate-600">
            <FiBriefcase size={14} className="inline mr-1" />
            {edital.total_inscritos} inscrito{edital.total_inscritos === 1 ? '' : 's'} até agora
          </div>
          {botaoInscrever()}
        </div>
      </div>
    </PortalPage>
  );
}
